import React, { useMemo } from "react";

/**
 * HexaGrid — partial molecular hexagon formation (never a full honeycomb).
 *
 * Renders a cluster of hexagons that fades out organically toward the
 * edges, with a few illuminated gold nodes at random vertices. Designed
 * to sit in a corner or emerge from a section edge — a "hidden scientific
 * architecture" atmosphere rather than a wallpaper.
 *
 * Props:
 *   width, height  — canvas size in px
 *   opacity        — overall opacity (default 0.35)
 *   nodeCount      — number of illuminated gold nodes (default 5)
 *   fadeFrom       — corner the network is anchored to (default "top-right")
 *   className      — pass-through for positioning
 */
export const HexaGrid = ({
    width = 480,
    height = 480,
    opacity = 0.35,
    nodeCount = 5,
    fadeFrom = "top-right",
    className = "",
}) => {
    const cols = 7;
    const rows = 8;
    const r = 24; // hex radius
    const hexW = r * 2;
    const hexH = Math.sqrt(3) * r;

    const hexes = useMemo(() => {
        const arr = [];
        for (let row = 0; row < rows; row += 1) {
            for (let col = 0; col < cols; col += 1) {
                const x = col * (hexW * 0.75);
                const y = row * hexH + (col % 2 ? hexH / 2 : 0);
                arr.push({ x, y, key: `${row}-${col}` });
            }
        }
        return arr;
    }, [rows, cols, hexW, hexH]);

    const nodes = useMemo(() => {
        // Deterministic pseudo-random nodes so SSR/hydration stay stable.
        const arr = [];
        for (let i = 0; i < nodeCount; i += 1) {
            const seed = (i * 9973) % hexes.length;
            const h = hexes[seed];
            arr.push({ x: h.x, y: h.y, r: 1.6 + (i % 2) * 0.6, key: `n${i}` });
        }
        return arr;
    }, [hexes, nodeCount]);

    const anchorX = fadeFrom.includes("right") ? "100%" : "0%";
    const anchorY = fadeFrom.includes("bottom") ? "100%" : "0%";

    return (
        <svg
            viewBox={`0 0 ${cols * hexW * 0.75 + hexW} ${rows * hexH + hexH}`}
            width={width}
            height={height}
            className={className}
            style={{ opacity }}
            aria-hidden="true"
            data-testid="hexa-grid"
        >
            <defs>
                <radialGradient id="hex-fade" cx={anchorX} cy={anchorY} r="90%">
                    <stop offset="0%"  stopColor="white" stopOpacity="1" />
                    <stop offset="55%" stopColor="white" stopOpacity="0.55" />
                    <stop offset="100%" stopColor="white" stopOpacity="0" />
                </radialGradient>
                <mask id={`hex-mask-${fadeFrom}`}>
                    <rect width="100%" height="100%" fill="url(#hex-fade)" />
                </mask>
                <linearGradient id="hex-line" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%"   stopColor="#E6C273" stopOpacity="0.75" />
                    <stop offset="100%" stopColor="#8E682E" stopOpacity="0.35" />
                </linearGradient>
                <radialGradient id="hex-node">
                    <stop offset="0%"   stopColor="#F1D68B" stopOpacity="1" />
                    <stop offset="100%" stopColor="#C89B4A" stopOpacity="0" />
                </radialGradient>
            </defs>

            <g mask={`url(#hex-mask-${fadeFrom})`} fill="none" stroke="url(#hex-line)" strokeWidth="0.6">
                {hexes.map((h) => {
                    const pts = Array.from({ length: 6 }).map((_, i) => {
                        const angle = (Math.PI / 3) * i;
                        const px = h.x + r + Math.cos(angle) * r;
                        const py = h.y + r + Math.sin(angle) * r;
                        return `${px.toFixed(1)},${py.toFixed(1)}`;
                    }).join(" ");
                    return <polygon key={h.key} points={pts} />;
                })}
            </g>

            {/* Illuminated gold nodes on top of the fade */}
            <g mask={`url(#hex-mask-${fadeFrom})`}>
                {nodes.map((n) => (
                    <circle
                        key={n.key}
                        cx={n.x + r}
                        cy={n.y + r}
                        r={n.r + 4}
                        fill="url(#hex-node)"
                    />
                ))}
                {nodes.map((n) => (
                    <circle
                        key={`c${n.key}`}
                        cx={n.x + r}
                        cy={n.y + r}
                        r={n.r}
                        fill="#F1D68B"
                        opacity="0.9"
                    />
                ))}
            </g>
        </svg>
    );
};

export default HexaGrid;
